/**
 * NCC: the delivery date picker in the bag drawer and on the cart page (snippets/ncc-delivery-date.liquid).
 *
 * The client's old shop let customers choose a future delivery date ("so if someone's birthday was coming up next
 * month, you could order it now and select a date closer to their birthday" - the proposal call) and a collection
 * date for click and collect. Shopify's own checkout pages cannot carry a custom field without Shopify Plus, so the
 * date is chosen here and saved on the cart as attributes, which Shopify copies onto the order (Additional details):
 *   - "Delivery method": "Delivery" or "Click and collect" (only while the dairy offers Pickup in store)
 *   - "Delivery date" or "Collection date": "Wednesday 30 September 2026" (the other one blank)
 *   - "First delivery date" instead, when the bag holds a Cheese Box Club subscription: the first box's date (Seal
 *     schedules the later boxes; the team moves them to follow it, QUESTIONS.md 51)
 *   - "First club box delivery date" (the third Wednesday the box is delivered on) when the club runs ONE delivery day a month (Theme settings > Delivery dates > Cheese Box
 *     Club dispatch = fixed): no calendar for the club box, the date follows from today (firstDispatch - the order-by
 *     day decides the month) and is shown as text; the other products in the same bag keep their delivery calendar
 * They are saved the moment they change (`cart/update.js`) and posted again by hidden fields with the Checkout button.
 * A posted item (a gift voucher sent by Royal Mail - snippets/ncc-posted.liquid) needs delivering but takes no date:
 * the snippet leaves it out of all of this, shows a note for it, and lists its variant in the hidden mirror
 * (`posted`) so the item stamp below skips it too.
 *
 * The one place a theme can put the date INSIDE Shopify's checkout without Plus is a line item property: checkout
 * prints non-underscore properties under each item. So when Checkout is pressed the date is also written on every
 * item that ships ("Delivery / collection date: Wednesday 30 September 2026" under each product in the order summary;
 * one neutral key, because the customer can still switch Ship / Pickup inside checkout, where nothing of ours runs,
 * and every bookable delivery day is also a collection day; a club first box says "First delivery date"), through
 * /cart/change.js, which keeps the other properties (a gift message) and the selling plan; the bag's own rendering
 * hides these keys (snippets/cart-products.liquid). Doing it at Checkout, not on every change, means no bag re-render
 * and no race with Horizon's quantity updates. An add to the bag copies the stamp the bag's items already carry (a
 * formdata hook), so a product added again merges with its line instead of splitting.
 *
 * Choosing Click and collect also tells Shopify the customer prefers Pick up, through the Storefront API's cart
 * delivery preference, so the checkout opens with Pick up and the dairy selected (the customer can still change it).
 *
 * The earliest bookable date is preselected and saved, so every order that needs delivering has a date; the rules
 * live in assets/ncc-delivery-dates.js and are re-checked when the calendar opens and when Checkout is pressed. A
 * date that has gone by then (a bag left open overnight) is moved to the earliest and the Checkout press is held
 * back with a message, so nobody reaches checkout with a date they did not see.
 *
 * Horizon morphs the bag after every change: the marked nodes keep their attributes but get the server's children
 * back, so a MutationObserver puts the live value back. A hidden mirror of the cart's saved values (unmarked, so the
 * morph refreshes it) tells the element when a date was changed elsewhere (another tab) and it adopts that date.
 *
 * The calendar is a modal <dialog> appended to <body> (the morph would strip children the server did not render;
 * a modal dialog is interactive wherever it sits). It follows the WAI-ARIA date picker dialog pattern: a grid of
 * day buttons with a roving tab stop, arrow keys by day and week, Home / End for the week, Page Up / Page Down by
 * month, Enter or Space to choose, Escape to close. While it is open a capture-phase Escape handler on the document
 * owns the key, so the bag drawer (which closes on a document Escape) stays open whatever holds focus.
 *
 * Loaded once per page (cart-drawer.liquid, main-cart.liquid), never inside the markup Horizon morphs.
 */
import {
  addDays,
  availableDays,
  buildCollectionRules,
  buildDispatchRules,
  buildRules,
  daysBetween,
  fillDispatchText,
  firstDispatch,
  formatDayMonth,
  formatLong,
  minutesNow,
  monthWeeks,
  noteFor,
  parseLong,
  today,
  weekday,
} from '@theme/ncc-delivery-dates';

const WEEKDAY_SHORT = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DESKTOP = window.matchMedia('(min-width: 750px)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
/** the dialog's transparent padding round the card (snippets/ncc-delivery-date.liquid .ncc-dd) */
const DIALOG_PAD = 12;
/** the Storefront API version for the cart delivery preference */
const STOREFRONT_API = 'api/2026-07/graphql.json';

/** "30 September" - the field shows the day and month only (Miles, 2026-09-24: shorter in the bag); the order keeps
 * the full "Wednesday 30 September 2026". */
function formatShort(/** @type {string} */ iso) {
  const parts = formatLong(iso).split(' ');
  return parts.slice(1, 3).join(' ');
}

/** @param {string} text */
function escapeHtml(text) {
  return text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c);
}

function root() {
  return window.Shopify?.routes?.root || '/';
}

/**
 * Writes attributes on the cart. One queue per page so the bag drawer's and the cart page's pickers never race.
 * @param {Record<string, string>} attributes @returns {Promise<boolean>}
 */
let queue = Promise.resolve(true);
function saveAttributes(attributes) {
  const url = (typeof Theme !== 'undefined' && Theme.routes?.cart_update_url) || `${root()}cart/update.js`;
  queue = queue
    .catch(() => false)
    .then(() =>
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      })
    )
    .then((response) => {
      if (!response.ok) throw new Error(`cart/update.js responded ${response.status}`);
      return true;
    })
    .catch((error) => {
      console.warn('[ncc-delivery-date]', error);
      return false;
    });
  return queue;
}

/** @param {AbortSignal} [signal] @returns {Promise<any>} the cart as /cart.js returns it */
async function getCart(signal) {
  const response = await fetch(`${root()}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' }, signal });
  if (!response.ok) throw new Error(`cart.js responded ${response.status}`);
  return response.json();
}

/**
 * Writes the date on every item that ships, as the line item property `key`, and drops the other date keys (a line
 * that switched from delivery to collection). Other properties (a gift message) are kept. A line that does not ship
 * (an emailed tour voucher) and, when `key` is null (the checkout date switched off), every line only loses our keys.
 * Shopify refuses an empty properties object once a line has properties, so a line left with nothing gets a hidden
 * underscore key, which checkout never prints.
 *
 * One line per request, re-reading the cart from each answer: a change can merge two lines that now match, which
 * renumbers the rest. A line is addressed by its key (a stale key fails instead of hitting a neighbour); a
 * subscription line by its position, because Shopify only accepts `line` together with `selling_plan`. An error
 * carries the last cart read (`error.cart`), so the caller can tell whether an item still shows another date.
 * With the club's fixed dispatch day (`skipPlans`), a club box is not sent with the rest: its line only loses our keys.
 * So does a posted item (`posted`: a gift voucher goes by Royal Mail and takes no date).
 * @param {string | null} key @param {string} value @param {string[]} keys every date key we own
 * @param {AbortSignal} [signal] @param {boolean} [skipPlans] @param {Set<string>} [posted] the posted variants' ids
 * @returns {Promise<any>} the cart afterwards
 */
async function stampLines(key, value, keys, signal, skipPlans = false, posted = new Set()) {
  const changeUrl = (typeof Theme !== 'undefined' && Theme.routes?.cart_change_url) || `${root()}cart/change.js`;
  /** @type {any} */
  let cart = null;
  try {
    cart = await getCart(signal);
    const dated = (/** @type {any} */ item) =>
      item.requires_shipping && !posted.has(String(item.variant_id)) && !(skipPlans && item.selling_plan_allocation);
    const stale = (/** @type {any} */ item) => {
      const props = item.properties || {};
      const ours = keys.some((k) => props[k] != null && props[k] !== '');
      if (!key || !dated(item)) return ours;
      return props[key] !== value || keys.some((k) => k !== key && props[k] != null && props[k] !== '');
    };
    for (let pass = 0; pass <= cart.items.length + 2; pass++) {
      const index = cart.items.findIndex(stale);
      if (index < 0) return cart;
      const item = cart.items[index];
      /** @type {Record<string, string>} */
      const properties = {};
      for (const [k, v] of Object.entries(item.properties || {})) if (!keys.includes(k) && v != null) properties[k] = /** @type {string} */ (v);
      if (key && dated(item)) properties[key] = value;
      if (!Object.keys(properties).length) properties._ncc = '-';
      const plan = item.selling_plan_allocation?.selling_plan?.id;
      const body = plan
        ? { line: index + 1, quantity: item.quantity, properties, selling_plan: plan }
        : { id: item.key, quantity: item.quantity, properties };
      const response = await fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        signal,
      });
      if (!response.ok) throw new Error(`cart/change.js responded ${response.status}`);
      cart = await response.json();
    }
    return cart;
  } catch (error) {
    throw Object.assign(error instanceof Error ? error : new Error(String(error)), { cart });
  }
}

/** Would checkout show a date other than the chosen one? From the last cart read, else from the hidden mirror. */
function showsOtherDate(/** @type {any} */ cart, /** @type {any} */ stamp, /** @type {string | null} */ key, /** @type {string} */ value, /** @type {string[]} */ keys, skipPlans = false, /** @type {Set<string>} */ posted = new Set()) {
  if (!cart) return Boolean(stamp?.value) && (stamp.key !== key || stamp.value !== value);
  return cart.items.some((/** @type {any} */ item) =>
    keys.some((k) => {
      const v = item.properties?.[k];
      const dated = item.requires_shipping && !posted.has(String(item.variant_id)) && !(skipPlans && item.selling_plan_allocation);
      return v != null && v !== '' && !(dated && k === key && v === value);
    })
  );
}

/** The stamp the cart's dated items carry and the variants carrying it, worked out as the snippet does. */
function stampOf(/** @type {any} */ cart, /** @type {string[]} */ keys, /** @type {Set<string>} */ posted = new Set()) {
  let stamp = { key: '', value: '' };
  /** @type {string[]} */
  const variants = [];
  for (const item of cart?.items || []) {
    if (!item.requires_shipping || posted.has(String(item.variant_id))) continue;
    for (const k of keys) {
      const v = item.properties?.[k];
      if (!v) continue;
      if (!stamp.key) stamp = { key: k, value: String(v) };
      variants.push(String(item.variant_id));
      break;
    }
  }
  return { ...stamp, variants };
}

/** Rewrites the stamp in every picker's hidden mirror, which the formdata hook reads (a bfcache restore keeps it). */
function writeStamp(/** @type {any} */ stamp) {
  document.querySelectorAll('ncc-delivery-date [data-ref="server"]').forEach((el) => {
    /** @type {any} */
    let data = {};
    try {
      data = JSON.parse(el.textContent || '{}') || {};
    } catch {}
    data.stamp = stamp;
    const text = JSON.stringify(data);
    if (el.textContent !== text) el.textContent = text;
  });
}

/**
 * Tells Shopify which delivery method the customer prefers, so checkout opens on it (Storefront API cart delivery
 * preference; it only prefills, the customer can still switch). Tokenless Storefront access covers cart writes. The
 * calls run one after another and only the latest wish is sent; checkout waits for them (capped).
 */
/** @type {{ pickup: boolean, locationId: string } | null} */
let preferenceWanted = null;
/** @type {Promise<void>} */
let preferenceQueue = Promise.resolve();
/** @param {boolean} pickup @param {string} locationId the pickup location's numeric id */
function setDeliveryPreference(pickup, locationId) {
  preferenceWanted = { pickup, locationId };
  preferenceQueue = preferenceQueue.then(async () => {
    const wanted = preferenceWanted;
    if (!wanted) return; // a later call already sent the latest wish
    preferenceWanted = null;
    try {
      const cart = await getCart();
      const delivery = wanted.pickup
        ? { deliveryMethod: ['PICK_UP'], pickupHandle: [wanted.locationId] }
        : { deliveryMethod: ['SHIPPING'], pickupHandle: [] };
      const response = await fetch(`${root()}${STOREFRONT_API}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          query:
            'mutation NccDeliveryPreference($id: ID!, $delivery: CartDeliveryPreferenceInput!) { cartBuyerIdentityUpdate(cartId: $id, buyerIdentity: { preferences: { delivery: $delivery } }) { userErrors { message } } }',
          variables: { id: `gid://shopify/Cart/${cart.token}`, delivery },
        }),
      });
      const json = await response.json();
      const errors = json?.errors || json?.data?.cartBuyerIdentityUpdate?.userErrors;
      if (!response.ok || errors?.length) console.warn('[ncc-delivery-date] delivery preference', errors || response.status);
    } catch (error) {
      console.warn('[ncc-delivery-date] delivery preference', error);
    }
  });
  return preferenceQueue;
}
/** capped, so a hung request never keeps the customer from checkout */
function preferenceSettled() {
  return Promise.race([preferenceQueue, new Promise((resolve) => setTimeout(resolve, 4000))]);
}

/** The cart's saved values and line stamp, as the server last rendered them (the hidden mirror of a picker). */
function readServer(/** @type {Element | null} */ host) {
  try {
    return JSON.parse(host?.querySelector('[data-ref="server"]')?.textContent || '{}') || {};
  } catch {
    return {};
  }
}

/**
 * The variants in the bag that are posted by Royal Mail (the gift vouchers), from the same mirror: they need
 * delivering but take no date, so they are never stamped (snippets/ncc-posted.liquid).
 * @param {Element | null} host @returns {Set<string>}
 */
function postedOf(host) {
  const list = readServer(host).posted;
  return new Set(Array.isArray(list) ? list.map(String) : []);
}

/**
 * A product added to the bag copies the date stamp its own shipping line already carries, so it merges with that
 * line instead of becoming a second line (lines that differ in properties stay apart). Anything else - a new
 * product, a ticket, a voucher - is added plain and stamped (or not) when Checkout is pressed. Capture phase on the
 * document: Horizon builds its add request with `new FormData(form)`, which fires formdata on the form.
 */
document.addEventListener(
  'formdata',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    const host = document.querySelector('ncc-delivery-date[data-line-property="true"]:not([data-mode="clear"]):not([hidden])');
    const stamp = readServer(host).stamp;
    const id = String(event.formData.get('id') || '');
    if (!stamp?.key || !stamp?.value || !id || !(stamp.variants || []).includes(id)) return;
    const name = `properties[${stamp.key}]`;
    if (!event.formData.has(name)) event.formData.set(name, stamp.value);
  },
  true
);

/** our own resubmission of a held Checkout press; another press while a hold runs is ignored */
let passThrough = false;
let stamping = false;

/** Checkout pressed and held while the items are stamped: the button waits, the bag cannot be edited. */
function holdCheckout(/** @type {HTMLElement | null} */ submitter, /** @type {boolean} */ on) {
  if (submitter) {
    submitter.toggleAttribute('data-ncc-checkout-busy', on);
    if (on) submitter.setAttribute('aria-busy', 'true');
    else submitter.removeAttribute('aria-busy');
  }
  document.querySelectorAll('cart-items-component').forEach((bag) => {
    bag.classList.toggle('cart-items-disabled', on);
    bag.querySelector('.cart-items')?.toggleAttribute('inert', on);
  });
}

// a page restored from the back-forward cache after a trip to checkout: nothing is waiting any more, and the items
// may carry a new stamp the mirror does not know yet
window.addEventListener('pageshow', (event) => {
  document.querySelectorAll('[data-ncc-checkout-busy]').forEach((el) => holdCheckout(/** @type {HTMLElement} */ (el), false));
  holdCheckout(null, false);
  stamping = false;
  if (event.persisted && document.querySelector('ncc-delivery-date [data-ref="server"]')) {
    const posted = postedOf(document.querySelector('ncc-delivery-date:not([data-mode="clear"])'));
    getCart()
      .then((cart) => writeStamp(stampOf(cart, ['Delivery / collection date', 'Delivery date', 'Collection date', 'First delivery date'], posted)))
      .catch(() => {});
  }
});


/**
 * Lets a held Checkout press go ahead: our own submit listeners stand aside for it. A form that no longer passes
 * its checks (the age box unticked meanwhile) releases the hold and shows the browser's message.
 * @param {HTMLFormElement} form @param {HTMLElement | null} submitter
 */
function resubmit(form, submitter) {
  if (!form.checkValidity()) {
    holdCheckout(submitter, false);
    stamping = false;
  }
  passThrough = true;
  try {
    const owned = (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) && submitter.form === form;
    if (owned) form.requestSubmit(/** @type {HTMLButtonElement} */ (submitter));
    else form.requestSubmit();
  } finally {
    passThrough = false;
  }
}

/**
 * Lines merged while stamping, so the form's quantity fields no longer match the bag: save the form's other fields
 * (the dates, the age gate's field, the note - the quantities are already on the cart) and go to checkout directly.
 * @param {HTMLFormElement} form
 */
function checkoutDirectly(form) {
  const data = new FormData(form);
  /** @type {Record<string, string>} */
  const attributes = {};
  for (const [name, value] of data) {
    const match = /^attributes\[(.+)\]$/.exec(name);
    if (match) attributes[match[1]] = String(value);
  }
  /** @type {Record<string, any>} */
  const body = { attributes };
  if (data.has('note')) body.note = String(data.get('note'));
  const url = (typeof Theme !== 'undefined' && Theme.routes?.cart_update_url) || `${root()}cart/update.js`;
  queue
    .then(() => fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }))
    .catch((error) => console.warn('[ncc-delivery-date] form fields', error))
    .finally(() => window.location.assign(`${root()}checkout`));
}

class DeliveryDate extends HTMLElement {
  /** @type {{ delivery: any, collect: any, first: any } | null} */
  #rulesByMode = null;
  /**
   * 'order' (a delivery or a collection), 'subscription' (a Cheese Box Club first box on a chosen date) or 'club'
   * (only club boxes to send, on the club's fixed dispatch day: no calendar)
   */
  #kind = 'order';
  /** the club's fixed dispatch day: '' (not in play), 'only' (only club boxes) or 'mix' (club boxes and other products) */
  #club = '';
  /** @type {ReturnType<typeof buildDispatchRules> | null} */
  #dispatchRules = null;
  /** the first club box's dispatch, worked out for today, and the one on show */
  #dispatch = { date: '', orderBy: '' };
  #dispatchShown = '';
  /** 'delivery' or 'collect' (order kind only) */
  #method = 'delivery';
  /** @type {string[]} */
  #days = [];
  /** @type {Set<string>} */
  #bookable = new Set();
  #today = '';
  /** the chosen day of the mode on show, YYYY-MM-DD */
  #selected = '';
  /** the day chosen in each mode, so switching back and forth keeps it */
  #chosen = { delivery: '', collect: '', first: '', club: '' };
  /** the cart's attributes as the server last rendered them (or as we last saved them) @type {Record<string, string>} */
  #server = {};
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** first day of the month on show, YYYY-MM-01 */
  #month = '';
  /** the day holding the grid's tab stop */
  #focusDay = '';
  /** @type {MutationObserver | null} */
  #observer = null;
  /** @type {ResizeObserver | null} */
  #cardObserver = null;
  /** @type {number | undefined} */
  #saveTimer;
  #scheduled = false;
  /** bumps on every check against the cart, so only the latest answer is adopted */
  #verify = 0;

  /** the attribute names, fixed English (the order admin and the emails read them) */
  get #names() {
    const d = this.dataset;
    return {
      method: d.attrMethod || 'Delivery method',
      delivery: d.attrDelivery || 'Delivery date',
      collect: d.attrCollect || 'Collection date',
      first: d.attrFirst || 'First delivery date',
      dispatch: d.attrDispatch || 'First club box delivery date',
      stamp: d.attrStamp || 'Delivery / collection date',
    };
  }

  /** @returns {'delivery' | 'collect' | 'first' | 'club'} */
  get #mode() {
    if (this.#kind === 'subscription') return 'first';
    if (this.#kind === 'club') return 'club';
    return this.#method === 'collect' ? 'collect' : 'delivery';
  }

  /** the rules of the calendar on show (none for a club-only bag, which has no calendar) */
  get #rules() {
    const mode = this.#mode;
    return this.#rulesByMode && mode !== 'club' ? this.#rulesByMode[mode] : null;
  }

  get #collectAvailable() {
    return this.#kind === 'order' && this.dataset.collect === 'true';
  }

  connectedCallback() {
    if (this.dataset.mode === 'clear') {
      // nothing in the bag needs a date any more: take the old ones off the cart (and the items at Checkout), and
      // stop checkout opening on Pick up
      const n = this.#names;
      if (this.dataset.anySaved === 'true') saveAttributes({ [n.method]: '', [n.delivery]: '', [n.collect]: '', [n.first]: '', [n.dispatch]: '' });
      if (this.dataset.savedMethod === (this.dataset.methodCollect || 'Click and collect')) setDeliveryPreference(false, '');
      document.addEventListener('submit', this.#onStrip, true);
      return;
    }
    const d = this.dataset;
    this.#kind = d.kind === 'subscription' || d.kind === 'club' ? d.kind : 'order';
    this.#club = d.club === 'only' || d.club === 'mix' ? d.club : '';
    this.#dispatchRules = this.#club ? buildDispatchRules({ week: d.clubWeek, weekday: d.clubWeekday, orderByDay: d.clubOrderBy }) : null;
    this.#rulesByMode = {
      delivery: buildRules({ weekdays: d.weekdays, prepDays: d.prepDays, windowDays: d.windowDays, holidays: d.holidays, closed: d.closed, notes: d.notes }),
      first: buildRules({
        weekdays: d.weekdays,
        prepDays: d.prepDays,
        windowDays: d.firstWindow,
        maxMonthDay: d.firstMaxDay,
        holidays: d.holidays,
        closed: d.closed,
        notes: d.notes,
      }),
      collect: buildCollectionRules({
        weekdays: d.collectWeekdays,
        prepDays: d.collectPrepDays,
        windowDays: d.collectWindow,
        openDates: d.collectOpenDates,
        closed: d.collectClosed,
        notes: d.collectNotes,
        cutoffDay: d.collectCutoffDay,
        cutoffHour: d.collectCutoffHour,
        bankHolidays: d.holidays,
        weekendBankHolidays: d.collectCutoffHolidays,
      }),
    };
    this.#server = readServer(this).attributes || {};
    const n = this.#names;
    const wasCollect = this.#server[n.method] === (d.methodCollect || 'Click and collect');
    this.#method = this.#collectAvailable && wasCollect ? 'collect' : 'delivery';
    // the cart said Click and collect but this bag cannot collect (a club box joined, pickup switched off): checkout
    // must stop opening on Pick up
    if (wasCollect && this.#method !== 'collect') setDeliveryPreference(false, '');
    this.#refresh();
    this.#chosen = {
      delivery: parseLong(this.#server[n.delivery] || '') || '',
      collect: parseLong(this.#server[n.collect] || '') || '',
      first: parseLong(this.#server[n.first] || '') || '',
      club: '',
    };
    const saved = this.#chosen[this.#mode];
    this.#selected = saved && this.#bookable.has(saved) ? saved : this.#days[0] || '';
    if (!this.#selected && this.#club === 'mix' && this.#dispatch.date) {
      // no delivery day can be booked for the other products (settings typed wrongly), but the club box's dispatch
      // does not depend on them: keep its note and its date, drop the calendar
      this.querySelectorAll('[data-ref="label"], [data-ref="field"], [data-help]').forEach((el) => {
        /** @type {HTMLElement} */ (el).hidden = true;
      });
      this.#dispatchShown = this.#dispatch.date;
      const desired = { [n.method]: '', [n.delivery]: '', [n.collect]: '', [n.first]: '', [n.dispatch]: formatLong(this.#dispatch.date) };
      for (const key of ['method', 'delivery', 'collect', 'first', 'dispatch']) {
        const input = /** @type {HTMLInputElement | null} */ (this.querySelector(`[data-ref="input-${key}"]`));
        if (input) input.value = desired[n[/** @type {'method'} */ (key)]];
      }
      this.#renderClub();
      if (Object.keys(desired).some((name) => (this.#server[name] || '') !== desired[name])) saveAttributes(desired);
      this.dataset.stripStamps = readServer(this).stamp?.key ? 'true' : this.dataset.stripStamps || '';
      document.addEventListener('submit', this.#onStrip, true);
      return;
    }
    if (!this.#selected) {
      // nothing bookable (settings typed wrongly): stay out of the way and send no date at all
      this.hidden = true;
      this.querySelectorAll('[data-ref^="input-"]').forEach((input) => {
        /** @type {HTMLInputElement} */ (input).value = '';
      });
      if (Object.values(this.#server).some(Boolean)) saveAttributes({ [n.method]: '', [n.delivery]: '', [n.collect]: '', [n.first]: '', [n.dispatch]: '' });
      this.dataset.stripStamps = readServer(this).stamp?.key ? 'true' : this.dataset.stripStamps || '';
      document.addEventListener('submit', this.#onStrip, true);
      return;
    }
    this.hidden = false;
    this.#chosen[this.#mode] = this.#selected;
    // the club box's dispatch as the customer first sees it; a later change (the order-by day passing while the bag
    // is open) is announced once at Checkout, however the line got updated meanwhile
    this.#dispatchShown = this.#dispatch.date;
    this.#render();
    // write the preselected date once the bag has settled (not in the same instant as Horizon's own cart requests)
    if (this.#needsSave()) this.#scheduleSave(600);
    if (this.#method === 'collect') this.#syncPreference();

    // delegated: Horizon's morph may swap the field or the form node, the listeners must not go with it
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('keydown', this.#onKeyModality);
    this.addEventListener('pointerdown', this.#onKeyModality);
    document.addEventListener('submit', this.#onSubmit, true);
    this.#observer = new MutationObserver(this.#onMutations);
    this.#observer.observe(this, { subtree: true, childList: true, characterData: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('keydown', this.#onKeyModality);
    this.removeEventListener('pointerdown', this.#onKeyModality);
    document.removeEventListener('submit', this.#onSubmit, true);
    document.removeEventListener('submit', this.#onStrip, true);
    this.#observer?.disconnect();
    this.#observer = null;
    window.clearTimeout(this.#saveTimer);
    this.#teardownDialog();
  }

  get #field() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-ref="field"]'));
  }

  /** @param {string} key @param {string} fallback */
  #t(key, fallback) {
    return this.dataset[key] || fallback;
  }

  /** the label of the date on show: Delivery date / Collection date / First box delivery date */
  get #label() {
    const d = this.dataset;
    if (this.#mode === 'first') return d.labelFirst || 'First box delivery date';
    if (this.#mode === 'collect') return d.labelCollect || 'Collection date';
    return d.label || 'Delivery date';
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (/** @type {HTMLElement} */ (event.target).closest?.('[data-ref="field"]')) this.#open();
  };

  /** the Delivery / Click and collect radios @param {Event} event */
  #onChange = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (input?.dataset?.ref !== 'method' || !input.checked) return;
    this.#setMethod(input.value === 'collect' ? 'collect' : 'delivery');
  };

  /**
   * WebKit stops matching :focus-visible once arrow keys move focus inside a radio group, so the switch remembers
   * keyboard use itself (the fieldset carries data-skip-node-update, the morph leaves the flag alone).
   * @param {Event} event
   */
  #onKeyModality = (event) => {
    const fieldset = this.querySelector('.ncc-delivery-date__method');
    if (!fieldset) return;
    const target = /** @type {HTMLElement} */ (event.target);
    if (event.type === 'keydown' && target?.dataset?.ref === 'method') fieldset.setAttribute('data-keyboard', '');
    else if (event.type === 'pointerdown') fieldset.removeAttribute('data-keyboard');
  };

  /** @param {'delivery' | 'collect'} method */
  #setMethod(method) {
    if (!this.#collectAvailable || method === this.#method) return;
    const previous = this.#method;
    this.#method = method;
    this.#refresh();
    const kept = this.#chosen[this.#mode];
    const next = kept && this.#bookable.has(kept) ? kept : this.#days[0];
    if (!next) {
      // no day can be booked this way (settings typed wrongly): stay as we were
      this.#method = previous;
      this.#refresh();
      this.#render();
      return;
    }
    this.#selected = next;
    this.#chosen[this.#mode] = next;
    this.#alert('');
    this.#render();
    this.#scheduleSave(0);
    this.#syncPreference();
  }

  #syncPreference() {
    const location = this.dataset.pickupLocation;
    if (!this.#collectAvailable || !location) return;
    setDeliveryPreference(this.#method === 'collect', location);
  }

  /**
   * After a morph: put the live value back into the field, then, if the mirror shows other values, check the real
   * cart (after our own saves) and adopt a date saved elsewhere (another tab). A section render requested before our
   * last save can arrive after it with the old values: the cart check keeps it from undoing the customer's choice.
   */
  #onMutations = () => {
    if (this.#scheduled) return;
    this.#scheduled = true;
    queueMicrotask(() => {
      this.#scheduled = false;
      this.#render();
      const server = readServer(this).attributes || {};
      const n = this.#names;
      const differs = Object.values(n).some((name) => (server[name] || '') !== (this.#server[name] || ''));
      if (!differs) return;
      const ticket = ++this.#verify;
      queue
        .then(() => getCart())
        .then((cart) => {
          if (ticket === this.#verify && this.isConnected) this.#adopt(cart.attributes || {});
        })
        .catch(() => {});
    });
  };

  /** @param {Record<string, string>} server the cart's attributes as /cart.js returns them */
  #adopt(server) {
    const n = this.#names;
    const changed = Object.values(n).some((name) => (server[name] || '') !== (this.#server[name] || ''));
    if (!changed) return;
    this.#server = { ...server };
    let methodChanged = false;
    if (this.#collectAvailable) {
      const method = server[n.method] === (this.dataset.methodCollect || 'Click and collect') ? 'collect' : 'delivery';
      if (server[n.method] && method !== this.#method) {
        this.#method = method;
        methodChanged = true;
        this.#refresh();
      }
    }
    const iso = parseLong(server[n[this.#mode]] || '');
    if (iso && iso !== this.#selected) {
      this.#refresh();
      if (this.#bookable.has(iso)) this.#selected = iso;
    }
    if (!this.#bookable.has(this.#selected) && this.#days[0]) this.#selected = this.#days[0];
    this.#chosen[this.#mode] = this.#selected;
    this.#render();
    if (methodChanged) this.#syncPreference();
  }

  /** Recomputes the bookable days (and the club box's dispatch) for today's UK date. */
  #refresh() {
    const now = new Date();
    const zone = this.dataset.timezone || 'Europe/London';
    this.#today = today(now, zone);
    if (this.#club && this.#dispatchRules) this.#dispatch = firstDispatch(this.#today, this.#dispatchRules);
    if (this.#kind === 'club') {
      // no calendar: the one "day" is the club box's dispatch
      this.#days = this.#dispatch.date ? [this.#dispatch.date] : [];
      this.#bookable = new Set(this.#days);
      return;
    }
    const rules = this.#rules;
    if (!rules) return;
    // the weekend collection order-by time needs the UK clock as well as the date
    this.#days = availableDays(this.#today, rules, minutesNow(now, zone));
    this.#bookable = new Set(this.#days);
  }

  /**
   * What the cart should hold: the date on show under its own name, the other names blank; with the club's fixed
   * dispatch day also the club box's "First club box delivery date" (blank otherwise, so switching back clears it).
   */
  #desired() {
    const n = this.#names;
    /** @type {Record<string, string>} */
    const out = { [n.method]: '', [n.delivery]: '', [n.collect]: '', [n.first]: '', [n.dispatch]: '' };
    if (this.#club && this.#dispatch.date) out[n.dispatch] = formatLong(this.#dispatch.date);
    if (this.#kind === 'club') return out;
    const value = formatLong(this.#selected);
    if (!value) return out;
    out[n[/** @type {'delivery' | 'collect' | 'first'} */ (this.#mode)]] = value;
    if (this.#collectAvailable) {
      out[n.method] = this.#method === 'collect' ? this.dataset.methodCollect || 'Click and collect' : this.dataset.methodDelivery || 'Delivery';
    }
    return out;
  }

  #needsSave() {
    const desired = this.#desired();
    return Object.keys(desired).some((name) => (this.#server[name] || '') !== desired[name]);
  }

  /** Puts the chosen date into the field, the hidden inputs and the note (idempotent - also runs after a morph). */
  #render = () => {
    if (!this.#selected) return;
    const mode = this.#mode;
    if (this.dataset.method !== this.#method) this.dataset.method = this.#method;
    const short = formatShort(this.#selected);
    const long = formatLong(this.#selected);
    const label = this.querySelector('[data-ref="label"]');
    if (label && label.textContent !== this.#label) label.textContent = this.#label;
    const value = this.querySelector('[data-ref="value"]');
    if (value && value.textContent !== short) value.textContent = short;
    const desired = this.#desired();
    const n = this.#names;
    for (const [key, name] of Object.entries({ method: n.method, delivery: n.delivery, collect: n.collect, first: n.first, dispatch: n.dispatch })) {
      const input = /** @type {HTMLInputElement | null} */ (this.querySelector(`[data-ref="input-${key}"]`));
      if (input && input.value !== desired[name]) input.value = desired[name];
    }
    this.#renderClub();
    this.querySelectorAll('[data-ref="method"]').forEach((radio) => {
      const input = /** @type {HTMLInputElement} */ (radio);
      const checked = input.value === this.#method;
      if (input.checked !== checked) input.checked = checked;
    });
    this.querySelectorAll('[data-help]').forEach((help) => {
      const hidden = /** @type {HTMLElement} */ (help).dataset.help !== mode;
      if (/** @type {HTMLElement} */ (help).hidden !== hidden) /** @type {HTMLElement} */ (help).hidden = hidden;
    });
    const field = this.#field;
    const aria = `${this.#label}: ${long}`;
    if (field && field.getAttribute('aria-label') !== aria) field.setAttribute('aria-label', aria);
    if (field) {
      const describedBy = [`${this.id}-note`, this.querySelector(`[data-help="${mode}"]`) ? `${this.id}-help-${mode}` : ''].filter(Boolean).join(' ');
      if (field.getAttribute('aria-describedby') !== describedBy) field.setAttribute('aria-describedby', describedBy);
    }
    const note = this.querySelector('[data-ref="note"]');
    const text = this.#rules ? noteFor(this.#selected, this.#rules) : '';
    if (note && note.textContent !== text) note.textContent = text;
  };

  /**
   * The club's fixed dispatch day: the line that stands in for the calendar, or the note above it, from the setting's
   * text - "[date]" the dispatch day ("Wednesday 21 October"), "[order_by]" the last day to order for it.
   */
  #renderClub() {
    if (!this.#club || !this.#dispatch.date) return;
    const line = this.querySelector('[data-ref="club"]');
    const template = this.dataset.clubText || 'Your first box arrives on [date].';
    const text = this.#dispatchRules ? fillDispatchText(template, this.#dispatch, this.#dispatchRules) : template;
    if (line && line.textContent !== text) line.textContent = text;
  }

  /** @param {number} delay */
  #scheduleSave(delay) {
    window.clearTimeout(this.#saveTimer);
    this.#saveTimer = window.setTimeout(() => this.#save(), delay);
  }

  #save() {
    const desired = this.#desired();
    if (!formatLong(this.#selected)) return queue;
    const n = this.#names;
    const collectValue = this.dataset.methodCollect || 'Click and collect';
    if (this.#server[n.method] === collectValue && desired[n.method] !== collectValue && !this.#collectAvailable) setDeliveryPreference(false, '');
    return saveAttributes(desired).then((ok) => {
      // the cart now holds our values: a morph that brings them back is not "a change made elsewhere"
      if (ok) this.#server = { ...this.#server, ...desired };
      return ok;
    });
  }

  /** @param {string} iso */
  #choose(iso) {
    if (!this.#bookable.has(iso)) return;
    this.#selected = iso;
    this.#chosen[this.#mode] = iso;
    this.#alert('');
    this.#render();
    this.#scheduleSave(0);
  }

  /** @param {string} text */
  #alert(text) {
    const alert = this.querySelector('[data-ref="alert"]');
    if (alert && alert.textContent !== text) alert.textContent = text;
  }

  /** the form a Checkout press must belong to */
  #ownsForm(/** @type {EventTarget | null} */ target) {
    const input = this.querySelector('[data-ref^="input-"]');
    const formId = input?.getAttribute('form') || this.dataset.form || 'cart-form';
    return target instanceof HTMLFormElement && target.id === formId && this.isConnected;
  }

  /**
   * Checkout pressed. (1) A bag left open past midnight may hold a date that has gone: move it to the earliest, say
   * so, and hold this press back so the customer sees the new date before going on. (2) Write the date on every item
   * that ships (see the top of the file; with the checkout date switched off, take old stamps away), then let the
   * press through. The bag is locked meanwhile, a second press is ignored, a stalled request gives up after 8s and a
   * failure is retried once; if an item would still show another date at checkout, the press is held with a message
   * instead. If lines merged on the way, the form's quantity fields no longer match the bag, so its other fields are
   * saved and the customer goes to checkout directly.
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    if (passThrough || event.defaultPrevented || this.hidden || !this.#ownsForm(event.target)) return;
    const form = /** @type {HTMLFormElement} */ (event.target);
    if (stamping) {
      event.preventDefault();
      return;
    }
    this.#refresh();
    if (this.#club && this.#dispatch.date && this.#dispatch.date !== this.#dispatchShown) {
      // the bag stayed open past the order-by day: the club box now goes in the next dispatch - say so first
      event.preventDefault();
      this.#dispatchShown = this.#dispatch.date;
      if (this.#kind === 'club') {
        this.#selected = this.#dispatch.date;
        this.#chosen.club = this.#selected;
      }
      this.#render();
      this.#save();
      this.#alert(this.#t('textClubMoved', 'Your Cheese Box Club box will now arrive on [date]. Press Checkout again to continue.').replace('[date]', formatDayMonth(this.#dispatch.date)));
      return;
    }
    if (!this.#bookable.has(this.#selected)) {
      event.preventDefault();
      const earliest = this.#days[0];
      if (!earliest) return;
      this.#selected = earliest;
      this.#chosen[this.#mode] = earliest;
      this.#render();
      this.#save();
      this.#alert(this.#t('textExpired', 'The date you chose can no longer be booked, so we have moved it to [date]. Check it and press Checkout again.').replace('[date]', formatLong(earliest)));
      this.#field?.focus();
      return;
    }
    const n = this.#names;
    const keys = [n.stamp, n.delivery, n.collect, n.first];
    // the item says "Delivery / collection date" whichever the bag chose (the method can still change in checkout);
    // a club first box keeps its own name; club boxes on the fixed dispatch day carry no date (skipPlans), so a
    // club-only bag writes nothing
    const skipPlans = Boolean(this.#club);
    const mode = this.#mode;
    const key = this.dataset.lineProperty !== 'true' || mode === 'club' ? null : mode === 'first' ? n.first : n.stamp;
    if (!key && this.dataset.stripStamps !== 'true') return;
    event.preventDefault();
    stamping = true;
    const submitter = /** @type {HTMLElement | null} */ (event.submitter);
    holdCheckout(submitter, true);
    this.#alert('');
    const value = formatLong(this.#selected);
    const lines = form.querySelectorAll('[name="updates[]"]').length;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 8000);
    // a posted item (a gift voucher) is in the bag for Royal Mail, not for the dated parcel: no date on its line
    const posted = postedOf(this);
    const attempt = () => stampLines(key, value, keys, controller.signal, skipPlans, posted);
    const saving = this.#needsSave() ? this.#save() : queue;
    Promise.all([saving, preferenceSettled()])
      .then(attempt)
      .catch((error) => {
        if (controller.signal.aborted) throw error;
        return new Promise((resolve) => window.setTimeout(resolve, 700)).then(attempt);
      })
      .then((cart) => {
        writeStamp(stampOf(cart, keys, posted));
        if (cart && lines && cart.items.length !== lines) checkoutDirectly(form);
        else resubmit(form, submitter);
      })
      .catch((error) => {
        console.warn('[ncc-delivery-date] line stamp', error);
        if (showsOtherDate(error?.cart, readServer(this).stamp, key, value, keys, skipPlans, posted)) {
          // checkout would print an old date under an item: hold the press rather than show the wrong date
          holdCheckout(submitter, false);
          stamping = false;
          this.#alert(this.#t('textStampFailed', "We couldn't add your date to the order. Please press Checkout again."));
          return;
        }
        // nothing wrong would show: the date is still on the order as an attribute, never keep the customer back
        resubmit(form, submitter);
      })
      .finally(() => window.clearTimeout(timer));
  };

  /**
   * Checkout pressed while this element offers no date (clear mode, nothing bookable) but items still carry a date
   * from an earlier Checkout press: take it off them first, so checkout does not print it.
   * @param {SubmitEvent} event
   */
  #onStrip = (event) => {
    if (passThrough || event.defaultPrevented || stamping || this.dataset.stripStamps !== 'true' || !this.#ownsForm(event.target)) return;
    const form = /** @type {HTMLFormElement} */ (event.target);
    event.preventDefault();
    stamping = true;
    const submitter = /** @type {HTMLElement | null} */ (event.submitter);
    holdCheckout(submitter, true);
    const n = this.#names;
    const lines = form.querySelectorAll('[name="updates[]"]').length;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 8000);
    queue
      .then(() => stampLines(null, '', [n.stamp, n.delivery, n.collect, n.first], controller.signal))
      .then((cart) => {
        if (cart && lines && cart.items.length !== lines) checkoutDirectly(form);
        else resubmit(form, submitter);
      })
      .catch((error) => {
        console.warn('[ncc-delivery-date] line stamp', error);
        resubmit(form, submitter);
      })
      .finally(() => window.clearTimeout(timer));
  };

  /* ---------------------------------------------------------------------------------------------------------- */
  /* calendar dialog                                                                                              */
  /* ---------------------------------------------------------------------------------------------------------- */

  #open = () => {
    this.#refresh();
    if (!this.#bookable.has(this.#selected)) {
      if (!this.#days[0]) return;
      this.#choose(this.#days[0]);
    }
    const dialog = this.#buildDialog();
    dialog.setAttribute('aria-label', this.#chooseText);
    this.#month = `${this.#selected.slice(0, 8)}01`;
    this.#focusDay = this.#selected;
    this.#renderMonth();
    dialog.classList.toggle('ncc-dd--motion', !REDUCED_MOTION.matches);
    dialog.showModal();
    this.#position();
    // the note under the grid can add lines: place the card again whenever its size changes
    this.#cardObserver ??= new ResizeObserver(() => this.#position());
    const card = dialog.querySelector('.ncc-dd__card');
    if (card) this.#cardObserver.observe(card);
    this.#focusGrid();
    window.addEventListener('resize', this.#position);
    document.addEventListener('scroll', this.#position, true);
    // capture phase on the document: runs before the bag drawer's own document Escape handler, whatever has focus
    document.addEventListener('keydown', this.#onDocumentKey, true);
  };

  /** @param {boolean} [restoreFocus] */
  #close = (restoreFocus = true) => {
    const dialog = this.#dialog;
    window.removeEventListener('resize', this.#position);
    document.removeEventListener('scroll', this.#position, true);
    document.removeEventListener('keydown', this.#onDocumentKey, true);
    this.#cardObserver?.disconnect();
    if (dialog?.open) dialog.close();
    if (restoreFocus) this.#field?.focus({ preventScroll: true });
  };

  /** @param {KeyboardEvent} event */
  #onDocumentKey = (event) => {
    if (event.key !== 'Escape' || !this.#dialog?.open) return;
    event.preventDefault();
    event.stopPropagation();
    this.#close();
  };

  #teardownDialog() {
    if (!this.#dialog) return;
    this.#close(false);
    this.#dialog.remove();
    this.#dialog = null;
  }

  #buildDialog() {
    if (this.#dialog) return this.#dialog;
    const id = `${this.id || 'ncc-dd'}-dialog`;
    const dialog = document.createElement('dialog');
    dialog.className = 'ncc-dd';
    dialog.id = id;
    dialog.setAttribute('aria-label', this.#t('textChoose', 'Choose a delivery date'));
    dialog.innerHTML = `
      <div class="ncc-dd__card">
        <div class="ncc-dd__head">
          <button type="button" class="ncc-dd__nav" data-nav="-1" aria-label="${escapeHtml(this.#t('textPrevious', 'Previous month'))}">
            <svg viewBox="0 0 8 12" width="8" height="12" aria-hidden="true" focusable="false"><path d="M6.5 11L1.5 6L6.5 1" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <h2 class="ncc-dd__month" id="${id}-month" aria-live="polite"></h2>
          <button type="button" class="ncc-dd__nav" data-nav="1" aria-label="${escapeHtml(this.#t('textNext', 'Next month'))}">
            <svg viewBox="0 0 8 12" width="8" height="12" aria-hidden="true" focusable="false"><path d="M1.5 11L6.5 6L1.5 1" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
        <table class="ncc-dd__grid" role="grid" aria-labelledby="${id}-month">
          <thead><tr>${WEEKDAY_SHORT.map((d) => `<th scope="col" abbr="${d}">${d}</th>`).join('')}</tr></thead>
          <tbody></tbody>
        </table>
        <div class="ncc-dd__foot">
          <p class="ncc-dd__note" aria-live="polite"></p>
          <button type="button" class="ncc-dd__close">${escapeHtml(this.#t('textClose', 'Close'))}</button>
        </div>
      </div>`;
    dialog.addEventListener('click', this.#onDialogClick);
    dialog.addEventListener('keydown', this.#onDialogKey);
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#close();
    });
    document.body.append(dialog);
    this.#dialog = dialog;
    return dialog;
  }

  /** the calendar's name: Choose a delivery date / a collection date / your first box date */
  get #chooseText() {
    if (this.#mode === 'first') return this.#t('textChooseFirst', 'Choose your first box date');
    if (this.#mode === 'collect') return this.#t('textChooseCollect', 'Choose a collection date');
    return this.#t('textChoose', 'Choose a delivery date');
  }

  /** first and last months that hold a bookable day, as YYYY-MM-01 */
  get #monthRange() {
    const first = this.#days[0] || this.#selected;
    const last = this.#days[this.#days.length - 1] || this.#selected;
    return { first: `${first.slice(0, 8)}01`, last: `${last.slice(0, 8)}01` };
  }

  #renderMonth() {
    const dialog = this.#dialog;
    if (!dialog || !this.#rules) return;
    const year = Number(this.#month.slice(0, 4));
    const month = Number(this.#month.slice(5, 7)) - 1;
    const title = dialog.querySelector('.ncc-dd__month');
    if (title) title.textContent = `${MONTH_NAMES[month]} ${year}`;
    const { first, last } = this.#monthRange;
    const prev = /** @type {HTMLButtonElement | null} */ (dialog.querySelector('[data-nav="-1"]'));
    const next = /** @type {HTMLButtonElement | null} */ (dialog.querySelector('[data-nav="1"]'));
    // disabling the focused arrow would drop focus to <body>: note it and hand focus to the grid afterwards
    const active = document.activeElement;
    let rescue = false;
    if (prev) {
      prev.disabled = this.#month <= first;
      if (prev.disabled && active === prev) rescue = true;
    }
    if (next) {
      next.disabled = this.#month >= last;
      if (next.disabled && active === next) rescue = true;
    }

    const unavailable = this.#t('textUnavailable', 'not available');
    const weeks = monthWeeks(year, month);
    // always six rows, so the card keeps its height from month to month (a blank row is hidden from assistive tech)
    const blankRows = Math.max(0, 6 - weeks.length);
    const blank = `<tr aria-hidden="true">${'<td><span class="ncc-dd__spacer"></span></td>'.repeat(7)}</tr>`;
    const rows = weeks
      .map(
        (week) =>
          `<tr>${week
            .map((day) => {
              if (!day) return '<td role="gridcell"></td>';
              const bookable = this.#bookable.has(day);
              const classes = ['ncc-dd__day'];
              if (day === this.#today) classes.push('ncc-dd__day--today');
              if (bookable && this.#rules && noteFor(day, this.#rules)) classes.push('ncc-dd__day--note');
              const label = `${formatLong(day)}${bookable ? '' : `, ${unavailable}`}`;
              return `<td role="gridcell"${day === this.#selected ? ' aria-selected="true"' : ''}><button type="button" class="${classes.join(' ')}" data-day="${day}" tabindex="-1" aria-label="${escapeHtml(label)}"${day === this.#selected ? ' aria-pressed="true"' : ''}${bookable ? '' : ' aria-disabled="true"'}>${Number(day.slice(8))}</button></td>`;
            })
            .join('')}</tr>`
      )
      .join('') + blank.repeat(blankRows);
    const body = dialog.querySelector('tbody');
    if (body) body.innerHTML = rows;
    this.#setTabStop();
    this.#showNote(this.#focusDay);
    if (rescue) this.#focusGrid();
  }

  /** the focused day keeps the grid's single tab stop; a day outside the month on show falls back to its first bookable day */
  #setTabStop() {
    const dialog = this.#dialog;
    if (!dialog) return;
    let target = dialog.querySelector(`[data-day="${this.#focusDay}"]`);
    if (!target) {
      target = dialog.querySelector('.ncc-dd__day:not([aria-disabled])') || dialog.querySelector('.ncc-dd__day');
      this.#focusDay = /** @type {HTMLElement | null} */ (target)?.dataset.day || this.#focusDay;
    }
    dialog.querySelectorAll('.ncc-dd__day').forEach((b) => b.setAttribute('tabindex', b === target ? '0' : '-1'));
  }

  #focusGrid() {
    const target = /** @type {HTMLElement | null} */ (this.#dialog?.querySelector('.ncc-dd__day[tabindex="0"]'));
    target?.focus({ preventScroll: true });
  }

  /** @param {string} day */
  #showNote(day) {
    const note = this.#dialog?.querySelector('.ncc-dd__note');
    if (!note || !this.#rules) return;
    const text = this.#bookable.has(day) ? noteFor(day, this.#rules) : '';
    if (note.textContent !== text) note.textContent = text;
  }

  /** Moves the grid focus to a day, switching month when needed, within the months that can be booked. @param {string} day */
  #moveTo(day) {
    const { first, last } = this.#monthRange;
    const month = `${day.slice(0, 8)}01`;
    if (month < first || month > last) return;
    this.#focusDay = day;
    if (month !== this.#month) {
      this.#month = month;
      this.#renderMonth();
    } else {
      this.#setTabStop();
      this.#showNote(day);
    }
    this.#focusGrid();
  }

  /** @param {MouseEvent} event */
  #onDialogClick = (event) => {
    const dialog = this.#dialog;
    if (!dialog) return;
    const target = /** @type {HTMLElement} */ (event.target);
    // a click on the ::backdrop or the dialog's padding round the card lands on the dialog element itself
    if (target === dialog) {
      this.#close();
      return;
    }
    const nav = /** @type {HTMLButtonElement | null} */ (target.closest('[data-nav]'));
    if (nav && !nav.disabled) {
      const step = Number(nav.dataset.nav);
      const date = new Date(Date.UTC(Number(this.#month.slice(0, 4)), Number(this.#month.slice(5, 7)) - 1 + step, 1));
      this.#month = date.toISOString().slice(0, 10);
      // keep the tab stop on the chosen day when it is in view, else the month's first bookable day
      this.#focusDay = this.#selected.startsWith(this.#month.slice(0, 8)) ? this.#selected : '';
      this.#renderMonth();
      return;
    }
    if (target.closest('.ncc-dd__close')) {
      this.#close();
      return;
    }
    const dayButton = /** @type {HTMLElement | null} */ (target.closest('[data-day]'));
    if (dayButton) {
      const day = dayButton.dataset.day || '';
      if (!this.#bookable.has(day)) {
        this.#moveTo(day);
        return;
      }
      this.#choose(day);
      this.#close();
    }
  };

  /** @param {KeyboardEvent} event */
  #onDialogKey = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest?.('[data-day]');
    if (!(button instanceof HTMLElement)) return;
    const day = button.dataset.day || '';
    const moves = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
      Home: 1 - weekday(day),
      End: 7 - weekday(day),
    };
    if (event.key in moves) {
      event.preventDefault();
      this.#moveTo(addDays(day, moves[/** @type {keyof typeof moves} */ (event.key)]));
      return;
    }
    if (event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault();
      const step = event.key === 'PageUp' ? -1 : 1;
      const target = new Date(Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1 + step, 1));
      const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
      target.setUTCDate(Math.min(Number(day.slice(8)), lastDay));
      this.#moveTo(target.toISOString().slice(0, 10));
    }
  };

  /**
   * Desktop: a small panel beside the field (above it when the field sits low, as in the bag), the card's edge
   * lined up with the field's. Phones: centred by CSS.
   */
  #position = () => {
    const dialog = this.#dialog;
    const field = this.#field;
    if (!dialog?.open || !field) return;
    if (!DESKTOP.matches) {
      dialog.style.removeProperty('top');
      dialog.style.removeProperty('bottom');
      dialog.style.removeProperty('left');
      dialog.classList.remove('ncc-dd--anchored');
      return;
    }
    dialog.classList.add('ncc-dd--anchored');
    const rect = field.getBoundingClientRect();
    const box = dialog.getBoundingClientRect();
    const gap = 8;
    const room = { below: window.innerHeight - rect.bottom - gap, above: rect.top - gap };
    const cardHeight = box.height - 2 * DIALOG_PAD;
    const below = room.below >= cardHeight || room.below >= room.above;
    if (below) {
      const top = Math.max(0, Math.min(rect.bottom + gap - DIALOG_PAD, window.innerHeight - box.height));
      dialog.style.bottom = 'auto';
      dialog.style.top = `${Math.round(top)}px`;
    } else {
      // above the field, held by its bottom edge: a taller card grows upwards, away from the field
      const bottom = Math.max(0, window.innerHeight - rect.top + gap - DIALOG_PAD);
      dialog.style.top = 'auto';
      dialog.style.bottom = `${Math.round(bottom)}px`;
    }
    let left = rect.left - DIALOG_PAD;
    left = Math.max(0, Math.min(left, window.innerWidth - box.width));
    dialog.style.left = `${Math.round(left)}px`;
  };

  /** days from today to the chosen date - exposed for tests */
  get leadDays() {
    return this.#selected ? daysBetween(this.#today, this.#selected) : -1;
  }
}

if (!customElements.get('ncc-delivery-date')) customElements.define('ncc-delivery-date', DeliveryDate);
